import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const PILLAR_LABEL = {
  strength: "STRENGTH (S)",
  speed: "SPEED (F)",
  explosive: "EXPLOSIVE (E)",
  stamina: "STAMINA (P)",
  control: "CONTROL (C)",
};

export default function Tests() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch the test catalog once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/tests-meta")
      .then((r) => setTests(r.data.tests || []))
      .finally(() => setLoading(false));
  }, []);

  const byPillar = tests.reduce((acc, t) => {
    (acc[t.pillar] = acc[t.pillar] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="tests-page">
      <div className="gps-tag mb-4">PROTOCOL</div>
      <h1 className="gps-heading text-4xl md:text-6xl">10 TESTS</h1>
      <p className="text-mute mt-4 max-w-2xl text-sm md:text-base">
        Objective. Measurable. Executable in any GPS-certified facility. Each test maps to one of the
        five physical pillars and contributes to your final GPS score and archetype.
      </p>

      {loading && (
        <div className="text-mute gps-mono text-xs mt-12">SCANNING...</div>
      )}

      <div className="mt-12 space-y-12">
        {["strength", "speed", "explosive", "stamina", "control"].map((p) => (
          byPillar[p]?.length > 0 && (
            <section key={p} data-testid={`tests-pillar-${p}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="gps-heading text-2xl md:text-3xl">{PILLAR_LABEL[p]}</h2>
                <span className="gps-mono text-mute text-[10px] tracking-[0.3em]">{byPillar[p].length} TESTS</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {byPillar[p].map((t, i) => (
                  <Link
                    key={t.key}
                    to={`/tests/${t.key}`}
                    className="contents"
                    data-testid={`test-card-${t.key}`}
                  >
                    <GlassPanel className="p-6 glow-hover cursor-pointer h-full group">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="gps-mono text-cyan text-[10px] tracking-[0.25em]">
                            {p.toUpperCase()} · TEST {String(i + 1).padStart(2, "0")}
                          </div>
                          <div className="gps-heading text-xl md:text-2xl mt-2 leading-tight">
                            {t.name.toUpperCase()}
                          </div>
                          <div className="text-mute text-xs mt-2 gps-mono tracking-[0.15em]">
                            UNIT · {t.unit.toUpperCase()} {t.lower_better ? "· LOWER IS BETTER" : "· HIGHER IS BETTER"}
                          </div>
                        </div>
                        <ArrowRight size={18} className="text-mute group-hover:text-white transition-colors mt-1 shrink-0" />
                      </div>
                      <div className="hud-divider my-4" />
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { l: "Q (0 PT)", v: `${t.qdr_M[0]}${t.unit}` },
                          { l: "D (50 PT)", v: `${t.qdr_M[1]}${t.unit}` },
                          { l: "R (100 PT)", v: `${t.qdr_M[2]}${t.unit}` },
                        ].map((s) => (
                          <div key={s.l}>
                            <div className="gps-label text-[9px]">{s.l}</div>
                            <div className="big-numeric text-white text-base mt-1">{s.v}</div>
                          </div>
                        ))}
                      </div>
                      <div className="text-mute text-[10px] gps-mono mt-3 tracking-[0.15em]">
                        BOUNDS SHOWN FOR MEN · WOMEN'S D VARIES
                      </div>
                    </GlassPanel>
                  </Link>
                ))}
              </div>
            </section>
          )
        ))}
      </div>
    </div>
  );
}
